// PatientReport.tsx
import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPersonWalking,
    faMoon,
    faChartLine,
    faHeartPulse,
    faLungs,
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Alert, Card, Col, Container, Row } from 'react-bootstrap';
import './Compass.css';
import AbnormalVitalsAnalysis from './AbnormalVitalsAnalysis';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin
);

const PrintStyles = () => (
    <style>
        {`
      @media print {
        /* Hide the Stanford Medicine banner/header */
        header, 
        .su-masthead, 
        .su-brand-bar {
          display: none !important;
        }
        /* Add some top margin to the container to prevent cutoff */
        .container {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
      }
    `}
    </style>
);

const MAX_DAYS_TO_DISPLAY = 30;

export interface ReportData {
    studyId: string;
    dailyKeyToData: Record<string, Record<string, number>>;
    dateToSurveyAvailability: Record<string, any>;
    compassDates: Date[];
}

interface PatientReportProps {
  reportData: ReportData | null;
  zScoresData: ZScoresResponse | null;
  isLoading: boolean;
  error: string | null;
  isPrintMode?: boolean;
}

interface ZScoreData {
    date: string;

    // Active daytime measurements zscores
    ACCELEROMETERS_STD_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACCELEROMETERS_STD_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACCELEROMETERS_STD_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_x_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_x_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_x_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_y_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_y_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_y_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_z_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_z_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_z_ACTIVE_DAYTIME_MIN_zscore?: number;
    ACTIVITY_COUNTS_ACTIVE_DAYTIME_AVG_zscore?: number;
    ACTIVITY_COUNTS_ACTIVE_DAYTIME_MAX_zscore?: number;
    ACTIVITY_COUNTS_ACTIVE_DAYTIME_MIN_zscore?: number;
    EDA_ACTIVE_DAYTIME_AVG_zscore?: number;
    EDA_ACTIVE_DAYTIME_MAX_zscore?: number;
    EDA_ACTIVE_DAYTIME_MIN_zscore?: number;
    MET_ACTIVE_DAYTIME_AVG_zscore?: number;
    MET_ACTIVE_DAYTIME_MAX_zscore?: number;
    MET_ACTIVE_DAYTIME_MIN_zscore?: number;
    PULSE_RATE_ACTIVE_DAYTIME_AVG_zscore?: number;
    PULSE_RATE_ACTIVE_DAYTIME_MAX_zscore?: number;
    PULSE_RATE_ACTIVE_DAYTIME_MIN_zscore?: number;
    STEP_COUNTS_zscore?: number;
    TEMPERATURE_ACTIVE_DAYTIME_AVG_zscore?: number;
    TEMPERATURE_ACTIVE_DAYTIME_MAX_zscore?: number;
    TEMPERATURE_ACTIVE_DAYTIME_MIN_zscore?: number;

    // Resting daytime and nighttime measurements zscores
    ACCELEROMETERS_STD_RESTING_DAYTIME_AVG_zscore?: number;
    ACCELEROMETERS_STD_RESTING_DAYTIME_MAX_zscore?: number;
    ACCELEROMETERS_STD_RESTING_DAYTIME_MIN_zscore?: number;
    ACCELEROMETERS_STD_RESTING_NIGHTTIME_AVG_zscore?: number;
    ACCELEROMETERS_STD_RESTING_NIGHTTIME_MAX_zscore?: number;
    ACCELEROMETERS_STD_RESTING_NIGHTTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_DAYTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_DAYTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_DAYTIME_MIN_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_NIGHTTIME_AVG_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_NIGHTTIME_MAX_zscore?: number;
    ACTIGRAPHY_COUNTS_vector_RESTING_NIGHTTIME_MIN_zscore?: number;

    // Additional resting measurements zscores
    FIRST_SLEEP_TIME_zscore?: number;
    TIME_IN_BED_zscore?: number;
    TOTAL_SLEEP_TIME_zscore?: number;
    WAKE_TIME_zscore?: number;
    PRV_RESTING_DAYTIME_AVG_zscore?: number;
    PRV_RESTING_DAYTIME_MAX_zscore?: number;
    PRV_RESTING_DAYTIME_MIN_zscore?: number;
    PRV_RESTING_NIGHTTIME_AVG_zscore?: number;
    PRV_RESTING_NIGHTTIME_MAX_zscore?: number;
    PRV_RESTING_NIGHTTIME_MIN_zscore?: number;

    // Data quality metrics
    data_quality_ratio?: number;
    measurements_available?: number;
    measurements_missing?: number;
    composite_abnormality?: number;
}


export interface ZScoresResponse {
  z_scores: {
    status: string;
    message: string;
    scores: ZScoreData[];
    data_quality: {
      total_days: number;
      days_with_complete_data: number;
      days_with_partial_data: number;
      days_with_no_data: number;
      average_data_quality_ratio: number;
      vital_categories: Record<string, string[]>;
    };
  };
}

const PatientReport: React.FC<PatientReportProps> = ({
  reportData,
  zScoresData,
  isLoading,
  error,
  isPrintMode = false,
}) => {
  const dates = reportData?.compassDates?.map(date => date.toISOString().split('T')[0]).sort() || [];
  const slicedDates = dates.slice(0, MAX_DAYS_TO_DISPLAY);
  const startDate = dates.length ? new Date(dates[0]) : new Date();
  const recordedEndDate = dates.length ? new Date(dates[dates.length - 1]) : new Date();
  const recordedDurationDays = Math.ceil((recordedEndDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  // If durationDays are longer than MAX_DAYS_TO_DISPLAY, we will cap at MAX_DAYS_TO_DISPLAY
  const durationDays = Math.min(recordedDurationDays, MAX_DAYS_TO_DISPLAY);
  const endDate = new Date(startDate.getTime() + durationDays * 24 * 60 * 60 * 1000);
   
  // Calculate health indicators
  const avgSteps = Math.round(
    Object.values(reportData?.dailyKeyToData['STEP_COUNTS'] || {})
      .filter(val => val > 0)
      .reduce((sum: number, val: number) => sum + val, 0) / 
    Object.values(reportData?.dailyKeyToData['STEP_COUNTS'] || {})
      .filter(val => val > 0).length || 0
  );

  const avgSleep = 
    Object.values(reportData?.dailyKeyToData['TOTAL_SLEEP_TIME'] || {})
      .filter(val => val > 0)
      .reduce((sum: number, val: number) => sum + val/60, 0) / 
    Object.values(reportData?.dailyKeyToData['TOTAL_SLEEP_TIME'] || {})
      .filter(val => val > 0).length || 0;

  const getEQ5DScore = (dtStr?: string): string => {
    if (dtStr && reportData?.dateToSurveyAvailability[dtStr]) {
      const survey = reportData?.dateToSurveyAvailability[dtStr];
      const total = Number(survey.eq_1) + 
                  Number(survey.eq_2) + 
                  Number(survey.eq_3) + 
                  Number(survey.eq_4) + 
                  Number(survey.eq_5);
      return total > 0 ? (total / 5).toFixed(2) : "-";
    }
    return "-";
  };

  // Calculate EQ5D scores for all dates
  const validEQ5DScores = dates
    .map((date: string) => {
      if (reportData?.dateToSurveyAvailability[date]) {
        const survey = reportData?.dateToSurveyAvailability[date];
        const score = Number(survey.eq_1) + 
                    Number(survey.eq_2) + 
                    Number(survey.eq_3) + 
                    Number(survey.eq_4) + 
                    Number(survey.eq_5);
                return (!isNaN(score) && score > 0) ? score / 5 : null;
            }
            return null;
        })
        .filter((score): score is number => score !== null);

    // Calculate average EQ5D score
    const avgQoL = validEQ5DScores.length > 0
        ? validEQ5DScores.reduce((sum, score) => sum + score, 0) / validEQ5DScores.length
        : 0;

    const pulseRateKeys = [
        'PULSE_RATE_ACTIVE_DAYTIME_AVG',
        'PULSE_RATE_RESTING_DAYTIME_AVG',
        'PULSE_RATE_RESTING_NIGHTTIME_AVG'
    ] as const;

  const avgPulseRate = Math.round(
    pulseRateKeys
      .flatMap(key => Object.values(reportData?.dailyKeyToData[key] || {}) as number[])
      .filter(val => val > 0)
      .reduce((sum: number, val: number) => sum + val, 0) / 
    pulseRateKeys
      .flatMap(key => Object.values(reportData?.dailyKeyToData[key] || {}))
      .filter(val => val > 0).length || 0
  );

  const respRateKeys = [
    'RESPIRATORY_RATE_RESTING_DAYTIME_AVG',
    'RESPIRATORY_RATE_RESTING_NIGHTTIME_AVG'
  ] as const;
  
  const avgRespRate = Math.round(
    respRateKeys
      .flatMap(key => Object.values(reportData?.dailyKeyToData[key] || {}) as number[])
      .reduce((sum: number, val: number) => sum + val, 0) / 
    respRateKeys
      .flatMap(key => Object.values(reportData?.dailyKeyToData[key] || {})).length || 0
  );

    const getStatusDetails = (metric: string, value: number) => {
        const thresholds = {
            steps: { low: 5000, moderate: 7500, active: 10000 },
            sleep: { low: 7, high: 9 },
            qol: { no_problems: 1.5, slight_problems: 2.5, moderate_problems: 3.5, severe_problems: 4.5}, // these are ceilings, i.e. qol<=1 means good in EQ5D scores (1-5 scale, lower is better)
            heartRate: { low: 60, high: 100 },
            respRate: { low: 12, high: 20 }
        };

    switch (metric) {
      case 'steps':
        return {
          status: value >= thresholds.steps.active ? 'Active' : value >= thresholds.steps.moderate ? 'Moderate-active' : value >= thresholds.steps.low ? 'Moderate' : 'Low',
          color: value >= thresholds.steps.moderate ? 'bg-green-100 text-green-800' : // Active and Moderate-active
                 value >= thresholds.steps.low? 'bg-yellow-100 text-yellow-800' : // Moderate
                 'bg-red-100 text-red-800', // Low
          message: `${value.toLocaleString()} /day`
        };
      case 'sleep':
        return {
          status: value >= thresholds.sleep.high ? 'High' : value >= thresholds.sleep.low ? 'Normal' : 'Low',
          color: value >= thresholds.sleep.low ? 'bg-green-100 text-green-800' : 
                 'bg-red-100 text-red-800',
          message: `${value.toFixed(1)} hours/day`
        };
      case 'qol':
        return {
          status: value <= thresholds.qol.no_problems ? 'No problems' : value <= thresholds.qol.slight_problems ? 'Slight problems' : value <= thresholds.qol.moderate_problems ? 'Moderate problems' : value <= thresholds.qol.severe_problems ? 'Severe problems' : 'Extreme problems',
          color: value <= thresholds.qol.no_problems ? 'bg-green-100 text-green-800' : 
                 value <= thresholds.qol.moderate_problems ? 'bg-yellow-100 text-yellow-800' : 
                 'bg-red-100 text-red-800',
          message: `${value.toFixed(1)}`
        };
      case 'heartRate':
        return {
          status: value >= thresholds.heartRate.high ? 'High' : value >= thresholds.heartRate.low ? 'Normal' : 'Low',
          color: value >= thresholds.heartRate.low && value <= thresholds.heartRate.high ? 
                 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800',
          message: `${value} BPM`
        };
      case 'respRate':
        return {
          status: value >= thresholds.respRate.high ? 'High' : value >= thresholds.respRate.low ? 'Normal' : 'Low',
          color: value >= thresholds.respRate.low && value <= thresholds.respRate.high ? 
                 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800',
          message: `${value} br/min`
        };
      default:
        return { status: 'unknown', color: 'bg-gray-100 text-gray-800', message: 'N/A' };
    }
  };

  const LoadingSpinner: React.FC = () => (
    <Row className={`g-${isPrintMode ? '1' : '4'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
      <Col className={isPrintMode ? 'px-1' : ''}>
        <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
          <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
            <div className="d-flex justify-content-center align-items-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  interface ErrorAlertProps {
    message: string;
  }

  const ErrorAlert: React.FC<ErrorAlertProps> = ({ message }) => (
    <Row className={`g-${isPrintMode ? '1' : '4'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
      <Col className={isPrintMode ? 'px-1' : ''}>
        <Alert variant="danger">
          {message}
        </Alert>
      </Col>
    </Row>
  );

    const chartBaseOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
            }
        },
        elements: {
            line: {
                borderWidth: 2,
                tension: 0.4
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    font: {
                        size: 13,
                        family: '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                    }
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                },
                ticks: {
                    font: {
                        size: 13,
                        family: '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                    }
                }
            }
        }
    };

return (
  <>
    <PrintStyles />
    <Container className={`${isPrintMode ? 'p-0 mx-0' : 'p-4'}`} style={isPrintMode ? { maxWidth: '100%' } : {}}>
      {/* Header */}
      <Row className={`${isPrintMode ? 'mt-1 mb-1' : 'mb-4'} mx-0`}>
        <Col className={isPrintMode ? 'px-1' : ''}>
          <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
            <Card.Body className={isPrintMode ? 'py-0 px-1' : 'p-3'}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <h1 className={`${isPrintMode ? 'h5 my-1' : 'h2'} mb-0`}>Participant Monitoring Report</h1>
                <span className={`badge bg-secondary ${isPrintMode ? 'small' : ''}`}>{reportData?.studyId}</span>
              </div>
              <Row className={isPrintMode ? 'small' : ''}>
                <Col md={6}>
                  <small className="text-muted">Monitoring Period</small>
                  <p className="mb-0">
                    {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                    <small className="text-muted ms-2">({durationDays + 1} days)</small>
                  </p>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Data Completion</small>
                  <p className="mb-0">
                    {Math.round((Object.keys(reportData?.dailyKeyToData['STEP_COUNTS'] || {}).length / durationDays) * 100)}% of days tracked
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

                {/* Key Metrics Summary */}
                <Row className={`g-${isPrintMode ? '1' : '3'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
                    {[
                        { name: 'Daily Steps', value: avgSteps, type: 'steps', icon: faPersonWalking },
                        { name: 'Sleep Duration', value: avgSleep, type: 'sleep', icon: faMoon },
                        { name: 'Quality of Life', value: avgQoL, type: 'qol', icon: faChartLine },
                        { name: 'Heart Rate', value: avgPulseRate, type: 'heartRate', icon: faHeartPulse },
                        { name: 'Respiratory Rate', value: avgRespRate, type: 'respRate', icon: faLungs }
                    ].map((metric, index) => {
                        const details = getStatusDetails(metric.type, metric.value);
                        return (
                            <Col key={index} className={isPrintMode ? 'px-1' : ''}>
                                <Card className={`h-100 ${isPrintMode ? 'shadow-none border-0' : ''}`}>
                                    <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
                                        <div className="d-flex align-items-center mb-1">
                                            <FontAwesomeIcon icon={metric.icon} className={`me-1 text-secondary ${isPrintMode ? 'small' : ''}`} />
                                            <h6 className="mb-0 small">{metric.name}</h6>
                                        </div>
                                        <div>
                                            <h3 className={`mb-1 ${isPrintMode ? 'h5' : ''}`}>{details.message}</h3>
                                            <small className={`${details.color}`}>
                                                {details.status.charAt(0).toUpperCase() + details.status.slice(1)}
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                {/* Quality of Life Score Chart */}
                <Row className={`g-${isPrintMode ? '1' : '4'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
                    <Col className={isPrintMode ? 'px-1' : ''}>
                        <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
                            <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
                                <h5 className={`${isPrintMode ? 'h6 mb-1' : 'mb-4'}`}>Self-Reported Quality of Life Score (EQ5D)</h5>
                                <div style={{
                                    height: isPrintMode ? '150px' : '300px',
                                    width: '100%',
                                    maxWidth: isPrintMode ? '100%' : undefined,
                                }}>
                                    <Line
                                        data={{
                                            labels: slicedDates,
                                            datasets: [{
                                                label: 'EQ5D Score',
                                                data: slicedDates.map(date => {
                                                    const score = getEQ5DScore(date);
                                                    return score === "-" ? null : Number(score);
                                                }),
                                                borderColor: '#0891b2',
                                                backgroundColor: 'rgba(8, 145, 178, 0.1)',
                                                fill: { target: { value: 5 } },
                                                tension: 0.4
                                            }]
                                        }}
                                        options={{
                                            ...chartBaseOptions,
                                            scales: {
                                                ...(chartBaseOptions.scales || {}),
                                                y: {
                                                  min: 0.5,
                                                  max: 5,
                                                  reverse: true,
                                                  title: {
                                                      display: false,
                                                  },
                                                  ticks: {
                                                      stepSize: 0.5,
                                                      callback: (value) => {
                                                          if (Number.isInteger(value)) {
                                                              const labels = {
                                                                  1: '1 (No problems)',
                                                                  2: '2 (Slight)',
                                                                  3: '3 (Moderate)',
                                                                  4: '4 (Severe)',
                                                                  5: '5 (Extreme)'
                                                              };
                                                              return labels[value as keyof typeof labels];
                                                          }
                                                          return '';
                                                      },
                                                      font: { size: isPrintMode ? 8 : 13 }
                                                  }
                                              }
                                            },
                                            plugins: {
                                                ...chartBaseOptions.plugins,
                                                title: {
                                                    display: false,
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (context) => {
                                                            const value = context.raw as number;
                                                            let label = 'EQ5D Score: ' + value.toFixed(2);
                                                            let severity = '';
                                                            if (value <= 1.5) severity = ' (no problems)';
                                                            else if (value <= 2.5) severity = ' (slight problems)';
                                                            else if (value <= 3.5) severity = ' (moderate problems)';
                                                            else if (value <= 4.5) severity = ' (severe problems)';
                                                            else severity = ' (extreme problems)';
                                                            return label + severity;
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

      {/* Daily Step Count Chart */}
      <Row className={`g-${isPrintMode ? '1' : '4'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
        <Col className={isPrintMode ? 'px-1' : ''}>
          <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
            <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
              <h5 className={`${isPrintMode ? 'h6 mb-1' : 'mb-4'}`}>Daily Step Count</h5>
              <div style={{ 
                height: isPrintMode ? '150px' : '300px',
                width: '100%',
                maxWidth: isPrintMode ? '100%' : undefined,
              }}>
                <Line
                  data={{
                    labels: slicedDates, // Limit to the last 30 days
                    datasets: [{
                      label: 'Steps',
                      data: slicedDates.map(date => {
                        const steps = reportData?.dailyKeyToData['STEP_COUNTS']?.[date] || 0;
                        return steps === 0 ? null : steps;
                      }),
                      borderColor: '#0d6efd',
                      backgroundColor: 'rgba(13, 110, 253, 0.1)',
                      fill: true,
                      tension: 0.4
                    }]
                  }}
                  options={{
                    ...chartBaseOptions,
                    plugins: {
                      ...chartBaseOptions.plugins,
                      title: {
                        display: false,
                      }
                    },
                    scales: {
                      ...(chartBaseOptions.scales || {}),
                      y: {
                        ...(chartBaseOptions.scales?.y || {}),
                        title: {
                          display: false,
                        }
                      }
                    }
                  } as ChartOptions<'line'>}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sleep Duration Chart */}
      <Row className={`g-${isPrintMode ? '1' : '4'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
        <Col className={isPrintMode ? 'px-1' : ''}>
          <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
            <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
              <h5 className={`${isPrintMode ? 'h6 mb-1' : 'mb-4'}`}>Nightly Sleep Duration</h5>
              <div style={{ 
                height: isPrintMode ? '150px' : '300px',
                width: '100%',
                maxWidth: isPrintMode ? '100%' : undefined,
              }}>
                <Line
                  data={{
                    labels: slicedDates,
                    datasets: [{
                      label: 'Sleep Hours',
                      data: slicedDates.map(date => {
                        const sleep = reportData?.dailyKeyToData['TOTAL_SLEEP_TIME']?.[date] || 0;
                        return sleep === 0 ? null : sleep / 60;
                      }),
                      borderColor: '#7c3aed',
                      backgroundColor: 'rgba(124, 58, 237, 0.1)',
                      fill: true,
                      tension: 0.4
                    }]
                  }}
                  options={{
                    ...chartBaseOptions,
                    plugins: {
                      ...chartBaseOptions.plugins,
                      title: {
                        display: false,
                      }
                    },
                    scales: {
                      ...(chartBaseOptions.scales || {}),
                      y: {
                        ...(chartBaseOptions.scales?.y || {}),
                        ticks: {
                          callback: (value) => `${value} hrs`
                        }
                      }
                    }
                  } as ChartOptions<'line'>}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Heart Rate Trends */}
      <Row className={`g-${isPrintMode ? '1' : '4'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
        <Col className={isPrintMode ? 'px-1' : ''}>
          <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
            <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
              <h5 className={`${isPrintMode ? 'h6 mb-1' : 'mb-4'}`}>Heart Rate</h5>
              <div style={{ 
                height: isPrintMode ? '150px' : '300px',
                width: '100%',
                maxWidth: isPrintMode ? '100%' : undefined,
              }}>
                <Line
                  data={{
                    labels: slicedDates,
                    datasets: [
                      {
                        label: 'Average Heart Rate',
                        data: slicedDates.map(date => {
                          const activeDay = reportData?.dailyKeyToData['PULSE_RATE_ACTIVE_DAYTIME_AVG']?.[date] || 0;
                          const restDay = reportData?.dailyKeyToData['PULSE_RATE_RESTING_DAYTIME_AVG']?.[date] || 0;
                          const restNight = reportData?.dailyKeyToData['PULSE_RATE_RESTING_NIGHTTIME_AVG']?.[date] || 0;
                          const validValues = [activeDay, restDay, restNight].filter(v => v > 0);
                          return validValues.length ? validValues.reduce((a, b) => a + b) / validValues.length : null;
                        }),
                        borderColor: '#dc2626',
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                        borderWidth: 2,
                        fill: false
                      },
                      {
                        label: 'Maximum Heart Rate',
                        data: slicedDates.map(date => {
                          const values = [
                            reportData?.dailyKeyToData['PULSE_RATE_ACTIVE_DAYTIME_MAX']?.[date],
                            reportData?.dailyKeyToData['PULSE_RATE_RESTING_DAYTIME_MAX']?.[date],
                            reportData?.dailyKeyToData['PULSE_RATE_RESTING_NIGHTTIME_MAX']?.[date]
                          ].filter((v): v is number => typeof v === 'number' && !isNaN(v));
                          return values.length ? Math.max(...values) : null;
                        }),
                        borderColor: '#dc2626',
                        borderDash: [5, 5],
                        borderWidth: 1,
                        fill: false
                      },
                      {
                        label: 'Minimum Heart Rate',
                        data: slicedDates.map(date => {
                          const values = [
                            reportData?.dailyKeyToData['PULSE_RATE_ACTIVE_DAYTIME_MIN']?.[date],
                            reportData?.dailyKeyToData['PULSE_RATE_RESTING_DAYTIME_MIN']?.[date],
                            reportData?.dailyKeyToData['PULSE_RATE_RESTING_NIGHTTIME_MIN']?.[date]
                          ].filter((v): v is number => typeof v === 'number' && !isNaN(v));
                          return values.length ? Math.min(...values) : null;
                        }),
                        borderColor: '#dc2626',
                        borderDash: [5, 5],
                        borderWidth: 1,
                        fill: false
                      }
                    ]
                  }}
                  options={{
                    ...chartBaseOptions,
                    scales: {
                      ...(chartBaseOptions.scales || {}),
                      y: {
                        ...(chartBaseOptions.scales?.y || {}),
                        title: {
                          display: true,
                          text: 'Heart Rate (BPM)',
                          font: { size: isPrintMode ? 8 : 13 }
                        }
                      }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Respiratory Rate Trends */}
      <Row className={`g-${isPrintMode ? '1' : '4'} ${isPrintMode ? 'mb-1' : 'mb-4'} mx-0`}>
        <Col className={isPrintMode ? 'px-1' : ''}>
          <Card className={`${isPrintMode ? 'shadow-none border-0' : ''}`}>
            <Card.Body className={isPrintMode ? 'p-1' : 'p-3'}>
              <h5 className={`${isPrintMode ? 'h6 mb-1' : 'mb-4'}`}>Respiratory Rate</h5>
              <div style={{ 
                height: isPrintMode ? '150px' : '300px',
                width: '100%',
                maxWidth: isPrintMode ? '100%' : undefined,
              }}>
                <Line
                  data={{
                    labels: slicedDates,
                    datasets: [
                      {
                        label: 'Average Respiratory Rate',
                        data: slicedDates.map(date => {
                          const restDay = reportData?.dailyKeyToData['RESPIRATORY_RATE_RESTING_DAYTIME_AVG']?.[date] || 0;
                          const restNight = reportData?.dailyKeyToData['RESPIRATORY_RATE_RESTING_NIGHTTIME_AVG']?.[date] || 0;
                          const validValues = [restDay, restNight].filter(v => v > 0);
                          return validValues.length ? validValues.reduce((a, b) => a + b) / validValues.length : null;
                        }),
                        borderColor: '#059669',
                        backgroundColor: 'rgba(5, 150, 105, 0.1)',
                        borderWidth: 1.5,
                        fill: false
                      },
                      {
                        label: 'Maximum Respiratory Rate',
                        data: slicedDates.map(date => {
                          const values = [
                            reportData?.dailyKeyToData['RESPIRATORY_RATE_RESTING_DAYTIME_MAX']?.[date],
                            reportData?.dailyKeyToData['RESPIRATORY_RATE_RESTING_NIGHTTIME_MAX']?.[date]
                          ].filter((v): v is number => typeof v === 'number' && !isNaN(v));
                          return values.length ? Math.max(...values) : null;
                        }),
                        borderColor: '#059669',
                        borderDash: [5, 5],
                        borderWidth: 1,
                        fill: false
                      },
                      {
                        label: 'Minimum Respiratory Rate',
                        data: slicedDates.map(date => {
                          const values = [
                            reportData?.dailyKeyToData['RESPIRATORY_RATE_RESTING_DAYTIME_MIN']?.[date],
                            reportData?.dailyKeyToData['RESPIRATORY_RATE_RESTING_NIGHTTIME_MIN']?.[date]
                          ].filter((v): v is number => typeof v === 'number' && !isNaN(v));
                          return values.length ? Math.min(...values) : null;
                        }),
                        borderColor: '#059669',
                        borderDash: [5, 5],
                        borderWidth: 1,
                        fill: false
                      }
                    ]
                  }}
                  options={{
                    ...chartBaseOptions,
                    scales: {
                      ...(chartBaseOptions.scales || {}),
                      y: {
                        ...(chartBaseOptions.scales?.y || {}),
                        title: {
                          display: true,
                          text: 'Respiratory Rate (br/min)',
                          font: { size: isPrintMode ? 8 : 13 }
                        }
                      }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Abnormal Vitals Analysis */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorAlert message={error} />
      ) : zScoresData && reportData && (
        <AbnormalVitalsAnalysis 
          data={zScoresData}
          dailyKeyToData={reportData.dailyKeyToData}
          isPrintMode={isPrintMode}
        />
      )}

            </Container>
        </>
    );
};

export default PatientReport;
